import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useCallback, useState } from 'react';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetNonProfitInsights } from '@/api/hooks/nonProfitOrgHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import ClaimNpoCard from '@/components/cards/ClaimNpoCard';
import DetailsHeader from '@/components/DetailsHeader';
import DonationFooterWeb from '@/components/DonationFooterWeb';
import DotsLoader from '@/components/DotsLoader';
import ClaimNpoModal from '@/components/modals/ClaimNpoModal';
import TabBarItemDetailsMaterial from '@/components/navigation/TabBarItemDetailsMaterial';
import { TopTabsReanimated } from '@/components/navigation/TopTabsReanimated';
import DonateFooter from '@/components/npo/details/DonateFooter';
import AuditItem from '@/components/npo/details/insights/AuditItem';
import BoardItem from '@/components/npo/details/insights/BoardItem';
import PLItem from '@/components/npo/details/insights/PLItem';
import RevenueItem from '@/components/npo/details/insights/RevenueItem';
import SalariesItem from '@/components/npo/details/insights/SalariesItem';
import SummaryItem from '@/components/npo/details/insights/SummaryItem';
import PlatformContainer from '@/components/web/PlatformContainer';
import { useAuth } from '@/contexts/authContext';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import PastorItem from '@/components/npo/details/insights/PastorItem';

export default function NpoDetailLayout() {
  const { width, height } = useWindowDimensions();
  const { bottom } = useSafeAreaInsets();
  const { state } = useAuth();
  const { npoId } = useLocalSearchParams<{
    npoId: string;
  }>();

  const { data, isPending, isLoading, refetch } = useGetNonProfitInsights({ id: npoId });
  const [containerWidth, setContainerWidth] = useState(0);
  const [claimModalVisible, setClaimModalVisible] = useState(false);

  const dataJson = JSON.stringify(data);

  // Only individuals canEdit npo profiles that are not claimed
  const canEditNotClaimed = state.authReady && state.user?.user.role === 'INDIVIDUAL' && data?.type === 'CHURCH';

  const getNotClaimedHeaderHeight = () => {
    if (Platform.OS !== 'web') return width;

    if (width >= 2560) return height * 0.45;
    if (width >= 1440) return height * 0.65;
    if (width >= 1024) return height * 0.75;
    if (width >= 768) return height * 0.5;

    return height * 0.4;
  };

  // FocusEffect to refetch data when we update something
  useFocusEffect(
    useCallback(() => {
      if (npoId) refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [npoId])
  );

  if (isPending || isLoading || data === undefined || !dataJson) {
    return (
      <PlatformContainer>
        <View style={styles.mainContainer}>
          <DotsLoader />
        </View>
      </PlatformContainer>
    );
  } else {
    return (
      <PlatformContainer
        {...(Platform.OS === 'web' && {
          ref: (node) => {
            if (node) setContainerWidth(node?.clientWidth - 160);
          }
        })}>
        <View
          style={[
            styles.content,
            {
              paddingBottom: !data.canReceiveDonations || state.user?.user.role === 'NON_PROFIT_ORG' ? bottom : 0
            }
          ]}>
          {data.claimed ? (
            // NPO - Claimed
            <TopTabsReanimated
              tabBar={(props) => <TabBarItemDetailsMaterial {...props} />}
              header={(animatedOpacityStyle) => (
                <DetailsHeader
                  title={data.title ?? ''}
                  type={'npo'}
                  thumbnails={safeJsonParseArray(data?.thumbnail ?? '[]')}
                  animatedOpacityStyle={animatedOpacityStyle}
                  itemId={data.id}
                  lightRatingValues={{
                    name: data.name,
                    ranking: parseFloat(data.lightRaitingScore ?? '0')
                  }}
                />
              )}
              headerOptions={
                Platform.OS === 'web'
                  ? {
                      maxHeight: width > 768 ? containerWidth / 2 : containerWidth,
                      minHeight: containerWidth / 2
                    }
                  : {
                      maxHeight: width,
                      minHeight: width >= 768 ? 90 : 60
                    }
              }
              screenOptions={{
                swipeEnabled: false
              }}>
              <TopTabsReanimated.Screen name="insights" initialParams={{ npoId: npoId }} />
              <TopTabsReanimated.Screen name="programs" />
              <TopTabsReanimated.Screen name="volunteer" />
            </TopTabsReanimated>
          ) : (
            // NPO - Not Claimed
            <>
              <View style={{ height: getNotClaimedHeaderHeight() }}>
                <DetailsHeader
                  title={data.title ?? ''}
                  type={'npo'}
                  thumbnails={safeJsonParseArray(data?.thumbnail ?? '[]')}
                  imageType={'churchToClaim'}
                  animatedOpacityStyle={{ opacity: 0 }}
                  itemId={data.id}
                />
                {/* Expanded ClaimNpoCard */}
                {width >= 1024 ? (
                  <View style={styles.claimCardContainer}>
                    <ClaimNpoCard
                      style={styles.customClaimCard}
                      npoName={data.name}
                      onButtonPress={() =>
                        router.push({
                          pathname: '/claimNpo',
                          params: {
                            npoName: data.name,
                            nonProfitOrgId: data.id
                          }
                        })
                      }
                    />
                  </View>
                ) : (
                  // ClaimNpoModal for smaller screens
                  <>
                    <ClaimNpoModal
                      npoName={data.name}
                      visible={claimModalVisible}
                      onCloseModal={() => setClaimModalVisible(false)}
                      onClaimPressed={() => {
                        setClaimModalVisible(false);
                        router.push({
                          pathname: '/claimNpo',
                          params: {
                            npoName: data.name,
                            nonProfitOrgId: data.id
                          }
                        });
                      }}
                    />
                    <BrandedButton
                      style={{
                        ...styles.claimButton,
                        bottom: (Platform.OS !== 'web' ? width : width >= 768 ? height * 0.6 : height * 0.4) * 0.4
                      }}
                      textProps={{ ellipsizeMode: 'middle', numberOfLines: 2 }}
                      textStyles={styles.buttonTextStyle}
                      backGroundColor="#FE9E22"
                      onPress={() => setClaimModalVisible(true)}>
                      {i18n.t('claim_npo.claim_button_label', { NPO_NAME: data.name })}
                    </BrandedButton>
                  </>
                )}
              </View>
              <ScrollView contentContainerStyle={styles.summary}>
                <SummaryItem canEdit={false} nonProfitOrg={data} />
                <RevenueItem canEdit={canEditNotClaimed} nonProfitOrg={data} />
                <SalariesItem canEdit={canEditNotClaimed} data={data.salaries} npoId={data.id} type={data.type} />
                {data.type === 'CHURCH' && (
                  <PastorItem canEdit={canEditNotClaimed} data={data.pastors} npoId={data.id} />
                )}
                <BoardItem canEdit={canEditNotClaimed} data={data.usersBoardDirectory} npoId={data.id} />
                <PLItem
                  canEdit={canEditNotClaimed}
                  pl={data.plStatement}
                  balanceSheet={data.balanceSheet}
                  npoId={data.id}
                />
                <AuditItem
                  canEdit={canEditNotClaimed}
                  thirdPartyAuditDeny={data.thirdPartyAuditDeny}
                  thirdPartyAudit={data.thirdPartyAudit}
                  npoId={data.id}
                />
              </ScrollView>
            </>
          )}
          {/* Donate Footer */}
          {data.canReceiveDonations &&
            !isPending &&
            state.authReady &&
            state.user?.user.role !== 'NON_PROFIT_ORG' &&
            (Platform.OS === 'web' ? (
              <DonationFooterWeb npoId={data.id} npoName={data?.name ?? '-'} />
            ) : (
              <DonateFooter npoId={data.id} npoName={data?.name ?? '-'} />
            ))}
        </View>
      </PlatformContainer>
    );
  }
}

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  content: {
    flex: 1
  },

  roundedIcon: {
    color: 'black',
    backgroundColor: 'white',
    //(Size + Padding + BorderWidth) / 2
    borderRadius: (18 + 16) / 2,
    padding: 8,
    overflow: 'hidden'
  },

  rightContainer: {
    flexDirection: 'row',
    columnGap: 15
  },

  claimButton: {
    position: 'absolute',
    left: 18,
    right: 18
  },

  buttonTextStyle: {
    alignSelf: 'center',
    textAlign: 'center',
    textAlignVertical: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10
  },

  claimCardContainer: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    left: 50,
    right: 50,
    top: 75,
    zIndex: 10
  },

  customClaimCard: {
    backgroundColor: 'white',
    borderColor: '#FE9E22',
    borderWidth: 1,
    flexDirection: 'column',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingBottom: 32,
    paddingTop: 40,
    alignItems: 'center',
    justifyContent: 'center'
  },

  summary: {
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0,
    paddingVertical: 18,
    gap: 20
  }
});
